import * as React from 'react';
import { Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQueryClient } from '@tanstack/react-query';
import { ScheduledEmail } from 'bloggers-backend/lib/emailScheduler/domain';
import { Result } from 'bloggers-backend/lib/domain/Result';
import { GridColDef, GridFilterModel, GridLogicOperator, GridSortModel } from '@mui/x-data-grid';
import Grid, { QueryOptions, QueryResult } from '../../Grid';
import { AppContext } from '../../../AppContext';
import { useUserTime } from '../../../hooks/useUserTime';
import { DeleteDiaglog } from '../../../components/DeleteDialog';

interface ScheduledEmailsListProps {
  scheduleId: string;
}

const INITIAL_PAGE_SIZE = 50;
const INITIAL_SORT_MODEL: GridSortModel = [{ field: 'createdAt', sort: 'asc' }];

export function ScheduledEmailsList({ scheduleId }: ScheduledEmailsListProps) {
  const { client, notificationsService } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const [toRemove, setToRemove] = React.useState<ScheduledEmail | null>(null);
  const formatUserTime = useUserTime();

  const cacheKey = `scheduledEmails-${scheduleId}`;

  const baseFilterModel: GridFilterModel = {
    logicOperator: GridLogicOperator.And,
    items: [{ field: 'scheduleId', operator: 'equals', value: scheduleId }],
  };

  const fetchData = async (options: QueryOptions): Promise<QueryResult> => {
    const result = await client<{}, Result<QueryResult>>({
      event: 'emailScheduler.getQueue',
      data: {
        cursor: options.cursor,
        pageSize: options.pageSize,
        filterModel: options.filterModel,
        sortModel: options.sortModel,
      },
    });

    if (result.success) {
      return result.value;
    }

    notificationsService.notify({
      severity: 'error',
      message: `Error: ${JSON.stringify(result.errors)}`,
    });

    return { data: [], pageInfo: {} };
  };

  const remove = async (id: string) => {
    const result = await client<{}, Result<unknown>>({
      event: 'emailScheduler.removeFromQueue',
      data: { id },
    });

    if (result.success) {
      notificationsService.notify({ severity: 'success', message: 'Email removed from queue' });
      await queryClient.invalidateQueries({ queryKey: [cacheKey] });
    } else {
      notificationsService.notify({
        severity: 'error',
        message: result.reason === 'validation' ? JSON.stringify(result.errors) : result.reason,
      });
    }
  };

  const columns: GridColDef[] = [
    { field: 'to', headerName: 'to', width: 220, filterable: true, sortable: true },
    { field: 'subject', headerName: 'subject', width: 220, filterable: true, sortable: true },
    { field: 'status', headerName: 'status', width: 110, filterable: true, sortable: true },
    {
      field: 'scheduledAt',
      headerName: 'scheduled',
      width: 300,
      filterable: true,
      sortable: true,
      valueGetter: (_value, row) => formatUserTime(row?.scheduledAt),
    },
    {
      field: 'createdAt',
      headerName: 'created',
      width: 300,
      filterable: true,
      sortable: true,
      valueGetter: (_value, row) => formatUserTime(row?.createdAt),
    },
    {
      field: 'sentAt',
      headerName: 'sent',
      width: 300,
      filterable: true,
      sortable: true,
      valueGetter: (_value, row) => formatUserTime(row?.sentAt),
    },
    {
      field: 'engine.attempts',
      headerName: 'attempts',
      width: 90,
      filterable: true,
      sortable: true,
      valueGetter: (_value, row) => row?.engine?.attempts,
    },
    { field: 'error', headerName: 'error', width: 280, filterable: true, sortable: true },
    {
      field: 'actions',
      headerName: 'actions',
      width: 90,
      filterable: false,
      sortable: false,
      renderCell: ({ row }: any) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setToRemove(row);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DeleteDiaglog
        isOpen={toRemove !== null}
        deleteWhat="email"
        userRejects={() => setToRemove(null)}
        userAccepts={() => {
          const email = toRemove;
          setToRemove(null);
          if (email) {
            void remove(email.id);
          }
        }}
      />
      <Box sx={{ flexGrow: 1 }}>
        <Grid
          cacheName={cacheKey}
          columns={columns}
          fetchData={fetchData}
          onRowClick={() => {}}
          baseFilterModel={baseFilterModel}
          initialSortModel={INITIAL_SORT_MODEL}
          initialPageSize={INITIAL_PAGE_SIZE}
        />
      </Box>
    </Box>
  );
}
